import { orderService, productService } from './firebase';
import { Order, OrderStatus } from '../types/models';
import { PreOrderKPIs, ProductPerformance, CategoryPerformance } from '../types/analytics';

export class AnalyticsService {
  
  async getPreOrderKPIs(): Promise<PreOrderKPIs> {
    try {
      const [ordersData, productsData] = await Promise.all([
        orderService.getOrders(),
        productService.getProducts()
      ]);

      // Filter orders by status and date ranges
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Recent orders (last 30 days)
      const recentOrders = ordersData.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= thirtyDaysAgo;
      });

      // Pre-orders (pending/confirmed/delivery_assigned status)
      const preOrders = recentOrders.filter(order => 
        order.status === OrderStatus.PENDING || 
        order.status === OrderStatus.CONFIRMED || 
        order.status === OrderStatus.DELIVERY_ASSIGNED
      );

      // Delivered orders for performance metrics
      const deliveredOrders = recentOrders.filter(order => 
        order.status === OrderStatus.DELIVERED
      );

      // Calculate core metrics
      const totalPreOrders = preOrders.length;
      const totalRevenue = recentOrders.reduce((sum, order) => sum + order.totalAmount, 0);
      const averageOrderValue = totalRevenue / recentOrders.length || 0;

      // Calculate conversion rate (delivered vs total orders)
      const preOrderConversionRate = recentOrders.length > 0 
        ? (deliveredOrders.length / recentOrders.length) * 100 
        : 0;

      // Calculate fulfillment rate
      const preOrderFulfillmentRate = preOrders.length > 0 
        ? ((preOrders.length - preOrders.filter(o => o.status === OrderStatus.CANCELLED).length) / preOrders.length) * 100
        : 0;

      // Calculate cancellation rate
      const cancelledOrders = recentOrders.filter(order => order.status === OrderStatus.CANCELLED);
      const preOrderCancellationRate = recentOrders.length > 0 
        ? (cancelledOrders.length / recentOrders.length) * 100 
        : 0;

      // Calculate customer retention (repeat customers)
      const customerOrderCounts = new Map<string, number>();
      recentOrders.forEach(order => {
        const count = customerOrderCounts.get(order.userId) || 0;
        customerOrderCounts.set(order.userId, count + 1);
      });
      const repeatCustomers = Array.from(customerOrderCounts.values()).filter(count => count > 1).length;
      const customerRetentionRate = customerOrderCounts.size > 0 
        ? (repeatCustomers / customerOrderCounts.size) * 100 
        : 0;

      // Product performance analysis
      const productStats = new Map<string, {
        orders: number;
        revenue: number;
        quantities: number;
      }>();

      recentOrders.forEach(order => {
        order.items.forEach(item => {
          // Handle both old structure (item.productId) and new structure (item.product)
          const productId = item.product?.id || item.productId;
          if (!productId) return;
          
          const stats = productStats.get(productId) || { orders: 0, revenue: 0, quantities: 0 };
          stats.orders += 1;
          
          // Use unitPrice if available, otherwise fallback to item.price or product.price
          const itemPrice = item.unitPrice || item.price || item.product?.price || 0;
          stats.revenue += itemPrice * item.quantity;
          stats.quantities += item.quantity;
          productStats.set(productId, stats);
        });
      });

      // Top selling products
      const topSellingProducts: ProductPerformance[] = Array.from(productStats.entries())
        .map(([productId, stats]) => {
          const product = productsData.find(p => p.id === productId);
          return {
            productId,
            productName: product?.name || 'Unknown Product',
            category: product?.category || 'Unknown',
            totalOrders: stats.orders,
            revenue: stats.revenue,
            averageRating: product?.rating || 0,
            demandTrend: (stats.orders > 10 ? 'increasing' : 'stable') as 'increasing' | 'stable' | 'decreasing',
            seasonality: 'medium' as 'high' | 'medium' | 'low',
            profitMargin: 0, // Requires cost tracking
            customerSatisfaction: Math.min(95, 80 + (product?.rating || 0) * 3)
          };
        })
        .sort((a, b) => b.totalOrders - a.totalOrders)
        .slice(0, 10);

      // Category performance - calculate growth rate from actual data
      const categoryStats = new Map<string, {
        orders: number;
        revenue: number;
        products: Set<string>;
      }>();
      
      // For growth rate calculation, get orders from 60-30 days ago
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      const previousPeriodOrders = ordersData.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= sixtyDaysAgo && orderDate < thirtyDaysAgo;
      });
      
      const previousCategoryStats = new Map<string, number>();
      previousPeriodOrders.forEach(order => {
        order.items.forEach(item => {
          const product = item.product || productsData.find(p => p.id === item.productId);
          if (!product) return;
          const category = product.category || 'Unknown';
          const itemPrice = item.unitPrice || item.price || product.price;
          previousCategoryStats.set(category, (previousCategoryStats.get(category) || 0) + (itemPrice * item.quantity));
        });
      });

      recentOrders.forEach(order => {
        order.items.forEach(item => {
          // Handle both old structure (item.productId) and new structure (item.product)
          const product = item.product || productsData.find(p => p.id === item.productId);
          const productId = item.product?.id || item.productId;
          
          if (!product || !productId) return;
          
          const category = product.category || 'Unknown';
          
          const stats = categoryStats.get(category) || { 
            orders: 0, 
            revenue: 0, 
            products: new Set() 
          };
          stats.orders += 1;
          
          // Use unitPrice if available, otherwise fallback to item.price or product.price
          const itemPrice = item.unitPrice || item.price || product.price;
          stats.revenue += itemPrice * item.quantity;
          stats.products.add(productId);
          categoryStats.set(category, stats);
        });
      });

      const categoryPerformance: CategoryPerformance[] = Array.from(categoryStats.entries())
        .map(([category, stats]) => {
          // Calculate real growth rate by comparing to previous period
          const previousRevenue = previousCategoryStats.get(category) || 0;
          const growthRate = previousRevenue > 0 
            ? ((stats.revenue - previousRevenue) / previousRevenue) * 100 
            : 0;
          
          return {
            category,
            totalOrders: stats.orders,
            revenue: stats.revenue,
            growthRate: Math.round(growthRate),
            averageOrderValue: stats.revenue / stats.orders || 0,
            customerPreference: stats.orders > 0 ? Math.min(100, 50 + (stats.orders * 2)) : 0,
            seasonalVariation: 0 // Requires year-over-year data
          };
        })
        .sort((a, b) => b.revenue - a.revenue);

      // Organic vs Conventional split
      const organicOrders = recentOrders.filter(order => 
        order.items.some(item => {
          const product = productsData.find(p => p.id === item.productId);
          return product?.isOrganic;
        })
      ).length;

      const organicVsConventionalSplit = {
        organic: recentOrders.length > 0 ? Math.round((organicOrders / recentOrders.length) * 100) : 0,
        conventional: recentOrders.length > 0 ? Math.round(((recentOrders.length - organicOrders) / recentOrders.length) * 100) : 0
      };

      // Seasonal trends (last 3 months)
      const seasonalDemandTrends = this.generateSeasonalTrends(recentOrders);

      // Peak ordering hours analysis
      const peakOrderingHours = this.analyzePeakHours(recentOrders);

      // Calculate financial metrics from real data
      // Gross margin = (Revenue - Cost) / Revenue * 100
      // Since we don't have cost data in orders, return 0 until cost tracking is implemented
      const grossMargin = 0;
      const operatingMargin = 0;
      const inventoryTurnover = 0;
      const cashFlowFromPreOrders = preOrders.reduce((sum, order) => sum + order.totalAmount, 0);

      // Calculate operational metrics from real data
      // Average lead time from order date to delivery date
      const deliveredOrdersWithDates = deliveredOrders.filter(order => 
        order.orderDate && order.deliveryDate
      );
      const averagePreOrderLeadTime = deliveredOrdersWithDates.length > 0
        ? deliveredOrdersWithDates.reduce((sum, order) => {
            const leadTime = (new Date(order.deliveryDate!).getTime() - new Date(order.orderDate).getTime()) / (1000 * 60 * 60 * 24);
            return sum + leadTime;
          }, 0) / deliveredOrdersWithDates.length
        : 0;
      
      const orderProcessingTime = 0; // Requires order processing timestamp tracking
      const deliveryEfficiency = 0; // Requires delivery performance data
      const wastePercentage = 0; // Requires waste tracking
      const packagingCostPerOrder = 0; // Requires cost tracking

      // Supply chain metrics - require external tracking systems
      const supplierReliabilityScore = 0;
      const freshnessSatisfactionScore = 0;
      // On-time delivery rate requires expectedDeliveryDate field in Order model
      const deliveryOnTimeRate = 0;
      const qualityComplaintRate = 0; // Requires complaint tracking system

      // Customer insights
      const customerAcquisitionCost = 0; // Requires marketing cost tracking
      const customerLifetimeValue = customerOrderCounts.size > 0
        ? totalRevenue / customerOrderCounts.size
        : 0;
      const repeatCustomerRate = customerRetentionRate;
      const averageCustomerOrderFrequency = customerOrderCounts.size > 0 
        ? Array.from(customerOrderCounts.values()).reduce((sum, count) => sum + count, 0) / customerOrderCounts.size 
        : 0;

      return {
        // Core Business Metrics
        totalPreOrders,
        totalRevenue,
        averageOrderValue,
        customerRetentionRate,
        
        // Pre-Order Specific Metrics
        preOrderConversionRate,
        preOrderFulfillmentRate,
        averagePreOrderLeadTime,
        preOrderCancellationRate,
        
        // Product Performance
        topSellingProducts,
        categoryPerformance,
        organicVsConventionalSplit,
        
        // Supply Chain Metrics
        supplierReliabilityScore,
        freshnessSatisfactionScore,
        deliveryOnTimeRate,
        qualityComplaintRate,
        
        // Customer Insights
        customerAcquisitionCost,
        customerLifetimeValue,
        repeatCustomerRate,
        averageCustomerOrderFrequency,
        
        // Seasonal Trends
        seasonalDemandTrends,
        peakOrderingHours,
        
        // Financial Health
        grossMargin,
        operatingMargin,
        inventoryTurnover,
        cashFlowFromPreOrders,
        
        // Operational Efficiency
        orderProcessingTime,
        deliveryEfficiency,
        wastePercentage,
        packagingCostPerOrder
      };

    } catch (error) {
      throw error;
    }
  }

  private generateSeasonalTrends(orders: Order[]) {
    const months = ['Jan', 'Feb', 'Mar'];
    const now = new Date();
    
    return months.map((month, index) => {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - (2 - index), 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - (2 - index) + 1, 1);
      
      const monthOrders = orders.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= monthDate && orderDate < nextMonth;
      });

      const revenue = monthOrders.reduce((sum, order) => sum + order.totalAmount, 0);
      const popularProducts = ['Tomatoes', 'Spinach', 'Carrots']; // Can be calculated from actual data

      return {
        month,
        orders: monthOrders.length,
        revenue,
        popularProducts,
        demandFactor: 0.8 + (index * 0.1)
      };
    });
  }

  private analyzePeakHours(orders: Order[]) {
    const hourStats = new Map<number, { count: number; total: number }>();
    
    orders.forEach(order => {
      const hour = new Date(order.orderDate).getHours();
      const stats = hourStats.get(hour) || { count: 0, total: 0 };
      stats.count += 1;
      stats.total += 1;
      hourStats.set(hour, stats);
    });

    const peakHours = [8, 12, 18]; // Common peak hours
    return peakHours.map(hour => ({
      hour,
      orderCount: hourStats.get(hour)?.count || 0,
      conversionRate: 68 + Math.floor(Math.random() * 10) // Simulated conversion rate
    }));
  }
}

export const analyticsService = new AnalyticsService();
